// Redux action creators
import {
  CREATE_AD,
  DELETE_AD,
  END_LOADING_AD,
  FETCH_ADS,
  START_LOADING_AD,
  UPDATE_AD,
} from "../constants/advertConstants";
import { toast } from "react-toastify";
import { getActiveCountry } from "../utilities/activeCountry.js";
import { getUserFromLocalStorage } from "../utilities/user.js";

// fetch() bypasses the global axios interceptors, so BOTH headers the shared
// API client normally adds must be attached explicitly to every ad request:
//   - `x-country`     : multi_national scoping (same as before)
//   - `Authorization` : the /advert admin CRUD routes are now protected with
//     auth + isAdmin (see routes/advertisementRoutes.js) — without the Bearer
//     token every create/update/delete would 401. Same localStorage profile
//     the axios interceptor reads. NOTE: unlike axios, these fetch calls have
//     no silent-refresh — an expired token surfaces as an error toast and the
//     admin re-authenticates via the normal session-expiry flow.
function withCountry(init = {}) {
  const profile = getUserFromLocalStorage();
  return {
    ...init,
    headers: {
      ...(init.headers || {}),
      "x-country": getActiveCountry(),
      ...(profile?.token ? { Authorization: `Bearer ${profile.token}` } : {}),
    },
  };
}

// Tolerate non-JSON server responses (e.g. proxy HTML 500 pages, gateway
// timeouts) so the caller always sees an actionable error string instead of
// "Unexpected token '<', '<!DOCTYPE'... is not valid JSON".
async function readResponse(response) {
  const text = await response.text();
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    const snippet = text.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 120);
    return {
      message: `Server returned ${response.status} ${response.statusText || "error"}${
        snippet ? ` — ${snippet}` : ""
      }`,
    };
  }
}

export const fetchAds = () => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING_AD });
    const response = await fetch("/advert/mobile-carousel", withCountry());
    const data = await readResponse(response);
    if (!response.ok) {
      throw new Error(data.message || "Failed to fetch advertisements");
    }
    dispatch({ type: FETCH_ADS, payload: data });
  } catch (error) {
    toast.error(error.message || "Failed to fetch advertisements");
  } finally {
    dispatch({ type: END_LOADING_AD });
  }
};

export const createAd = (adData) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING_AD });
    const response = await fetch("/advert/mobile-carousel", withCountry({
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(adData),
    }));
    const data = await readResponse(response);
    if (!response.ok) {
      throw new Error(data.message || "Failed to create advertisement");
    }
    dispatch({ type: CREATE_AD, payload: data });
    dispatch(fetchAds());
    toast.success(data.message || "Advertisement created successfully");
    return { ok: true, ad: data };
  } catch (error) {
    toast.error(error.message || "Failed to create advertisement");
    // The form stays open on failure and shows this next to the Save button,
    // so a rejected artwork or link can be fixed without re-uploading.
    return { ok: false, message: error.message || "Failed to create advertisement" };
  } finally {
    dispatch({ type: END_LOADING_AD });
  }
};

export const updateAd = (id, adData) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING_AD });
    const response = await fetch(`/advert/mobile-carousel/${id}`, withCountry({
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(adData),
    }));
    const data = await readResponse(response);
    if (!response.ok) {
      throw new Error(data.message || "Failed to update advertisement");
    }
    dispatch({ type: UPDATE_AD, payload: data });
    dispatch(fetchAds());
    toast.success(data.message || "Advertisement updated successfully");
    return { ok: true, ad: data };
  } catch (error) {
    toast.error(error.message || "Failed to update advertisement");
    return { ok: false, message: error.message || "Failed to update advertisement" };
  } finally {
    dispatch({ type: END_LOADING_AD });
  }
};

export const deleteAd = (id) => async (dispatch) => {
  try {
    dispatch({ type: START_LOADING_AD });
    const response = await fetch(`/advert/mobile-carousel/${id}`, withCountry({ method: "DELETE" }));
    const data = await readResponse(response);
    if (!response.ok) {
      throw new Error(data.message || "Failed to delete advertisement");
    }
    dispatch({ type: DELETE_AD, payload: id });
    toast.success(data.message || "Advertisement deleted successfully");
  } catch (error) {
    toast.error(error.message || "Failed to delete advertisement");
  } finally {
    dispatch({ type: END_LOADING_AD });
  }
};

// Typeahead for the Ad Center link picker: products and shops by name, every
// vertical, scoped to the active market. Plain async helper (not a thunk) so
// the form can debounce it locally. Resolves { products: [], vendors: [] }.
export const searchLinkOptions = async (q, kind = "") => {
  const params = new URLSearchParams({ q: q || "" });
  if (kind) params.set("kind", kind);
  const response = await fetch(`/advert/link-options?${params.toString()}`, withCountry());
  const data = await readResponse(response);
  if (!response.ok) {
    throw new Error(data.message || "Search failed");
  }
  return data;
};

// Ask the server to sign a Cloudinary upload for one artwork. Signed uploads
// bypass the unsigned preset and its 2000px incoming limit, which is what
// shrank 3159×498 desktop strips to 2000×315. Resolves the intent
// ({ mode, expected, cloudinary: { uploadUrl, apiKey, params } }).
export const requestArtworkUploadIntent = async ({ slot, surface, fileName }) => {
  const response = await fetch("/advert/upload-intent", withCountry({
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ slot, surface, fileName }),
  }));
  const data = await readResponse(response);
  if (!response.ok) {
    throw new Error(data.message || "Could not prepare the upload");
  }
  return data;
};
